/**
 * Settings Privacy - AJAX Field Handler
 * Handles automatic saving of privacy settings when fields are changed
 *
 * @author InterPals Development Team
 * @version 1.0.0
 */

(function() {
    'use strict';

    // Helper to get translation (falls back to key if i18n not available)
    function t(key) {
        return window.t ? window.t(key) : key;
    }

    // The shared AJAX switch/select handlers serve any settings section that
    // renders privacy_checkbox_row controls. That is the Privacy section and
    // the Premium section (the hide_ads toggle). initPrivacyFields binds by CSS
    // class, so only the controls present in the active section get wired up.
    var isSettingsPage = window.location.pathname.includes('/settings');
    var search = window.location.search;
    var hasAjaxControls = search.includes('section=privacy') || search.includes('section=premium');

    if (!isSettingsPage || !hasAjaxControls) {
        return;
    }

    // Use shared helpers
    var helpers = window.SettingsHelpers;
    if (!helpers) {
        console.error('SettingsHelpers not loaded');
        return;
    }

    var REQUEST_TIMEOUT = 10000; // 10 seconds

    var CSS_CLASSES = {
        AJAX_SELECT: 'ajax-privacy-select',
        AJAX_SWITCH: 'ajax-privacy-switch'
    };

    // Fields whose effect is visible in the current page's server-rendered
    // markup (the ad leaderboard). After a successful save the page reloads so
    // the rendered ads match the saved preference. The AJAX call persists the
    // value, but ad slots already in the DOM only pick it up on a fresh load.
    var RELOAD_AFTER_SAVE_FIELDS = ['hide_ads'];

    // Fallback delay (ms) for the post-save reload when the success-feedback
    // duration is unavailable. Lets the toggle animation and the green success
    // flash settle before the page reloads.
    var DEFAULT_RELOAD_DELAY_MS = 1200;

    // Set once a reload-triggering save succeeds. Further field edits are
    // ignored from that point so their in-flight save isn't aborted by the
    // imminent navigation (the switch UI stays interactive after a save).
    var reloadPending = false;

    /**
     * Handle select field change with AJAX
     * @param {HTMLSelectElement} select - The select element
     */
    function handleSelectChange(select) {
        var fieldName = select.getAttribute('data-field');
        var spinnerElement = document.getElementById(fieldName + '-spinner');
        var container = select.closest('.privacy-setting-row') || select.closest('.privacy-card');

        if (!fieldName) {
            return;
        }

        // A reload is already committed for this page. Skip the save so it
        // isn't aborted mid-flight by the navigation.
        if (reloadPending) {
            return;
        }

        var newValue = select.value;
        var originalValue = select.getAttribute('data-original-value') || newValue;

        helpers.sendFieldUpdate({
            fieldName: fieldName,
            fieldValue: newValue,
            container: container,
            field: select,
            spinner: spinnerElement,
            timeout: REQUEST_TIMEOUT,
            onSuccess: function(data) {
                // Update original value
                select.setAttribute('data-original-value', data.value);
            },
            onError: function() {
                // Revert to original value
                select.value = originalValue;
            }
        });
    }

    /**
     * Update status badge appearance based on checked state
     * @param {HTMLElement} badge - The status badge element
     * @param {boolean} isChecked - Whether the switch is checked
     */
    function updateStatusBadge(badge, isChecked) {
        if (!badge) return;

        badge.classList.remove('bg-green-100', 'text-green-700', 'bg-gray-100', 'text-gray-600');

        if (isChecked) {
            badge.classList.add('bg-green-100', 'text-green-700');
            badge.textContent = t('common.privacy_visible');
        } else {
            badge.classList.add('bg-gray-100', 'text-gray-600');
            badge.textContent = t('common.privacy_hidden');
        }
    }

    /**
     * Handle switch toggle change with AJAX
     * @param {HTMLInputElement} switchInput - The switch input element
     */
    function handleSwitchChange(switchInput) {
        var fieldName = switchInput.getAttribute('data-field');
        var spinnerElement = document.getElementById(fieldName + '-spinner');
        var container = switchInput.closest('.privacy-setting-row') || switchInput.closest('.privacy-card');

        if (!fieldName) {
            return;
        }

        // A reload is already committed for this page. Skip the save so it
        // isn't aborted mid-flight by the navigation.
        if (reloadPending) {
            return;
        }

        var newState = switchInput.checked;
        var originalState = !newState;
        var statusBadge = container ? container.querySelector('.status-badge') : null;

        helpers.sendFieldUpdate({
            fieldName: fieldName,
            fieldValue: newState ? '1' : '0',
            container: container,
            field: switchInput,
            spinner: spinnerElement,
            timeout: REQUEST_TIMEOUT,
            onSuccess: function(data) {
                // Ensure state matches server response
                var finalState = Boolean(parseInt(data.value));
                switchInput.checked = finalState;
                updateStatusBadge(statusBadge, finalState);

                // Fields that change the current page's server-rendered markup
                // (the ad leaderboard) reload so the rendered ads match the
                // saved value. The reload waits for the success flash to finish.
                if (RELOAD_AFTER_SAVE_FIELDS.indexOf(fieldName) !== -1) {
                    reloadPending = true;
                    var reloadDelay = (helpers.FEEDBACK_DURATION && helpers.FEEDBACK_DURATION.SUCCESS)
                        || DEFAULT_RELOAD_DELAY_MS;
                    setTimeout(function() {
                        // Clear the guard if navigation is blocked (e.g. a
                        // SecurityError) so field saves are not stuck disabled.
                        try {
                            window.location.reload();
                        } catch (e) {
                            reloadPending = false;
                        }
                    }, reloadDelay);
                }
            },
            onError: function() {
                // Revert state on error
                switchInput.checked = originalState;
                updateStatusBadge(statusBadge, originalState);
            }
        });
    }

    /**
     * Initialize AJAX privacy field handlers
     */
    function initPrivacyFields() {
        // Initialize hidden inputs with original values
        var ajaxSelects = document.querySelectorAll('.' + CSS_CLASSES.AJAX_SELECT);
        ajaxSelects.forEach(function(select) {
            // Store original value
            select.setAttribute('data-original-value', select.value);
        });

        // Listen for inline select change events on each container
        var inlineSelectContainers = document.querySelectorAll('.inline-select-container');
        inlineSelectContainers.forEach(function(container) {
            container.addEventListener('inlineSelectChange', function() {
                var hiddenInput = container.querySelector('.' + CSS_CLASSES.AJAX_SELECT);
                if (hiddenInput) {
                    handleSelectChange(hiddenInput);
                }
            });
        });

        // Initialize switch fields
        var ajaxSwitches = document.querySelectorAll('.' + CSS_CLASSES.AJAX_SWITCH);
        ajaxSwitches.forEach(function(switchInput) {
            switchInput.addEventListener('change', function() {
                handleSwitchChange(this);
            });
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPrivacyFields);
    } else {
        initPrivacyFields();
    }

})();
